import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, Lock } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString("es-SV", { dateStyle: "medium", timeStyle: "short" }) : "—");

const CHANGED = "incident-config-changed";

const useIncidentConfig = () => {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/institution/incident-responsible`, { withCredentials: true });
      setCfg(data);
    } catch { setCfg(null); }
  }, []);
  useEffect(() => {
    load();
    window.addEventListener(CHANGED, load);
    return () => window.removeEventListener(CHANGED, load);
  }, [load]);
  const notify = useCallback(() => window.dispatchEvent(new Event(CHANGED)), []);
  return [cfg, notify];
};

export const IncidentConfigNotice = () => {
  const [cfg] = useIncidentConfig();
  if (!cfg || cfg.valid) return null;
  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="incident-config-notice">
      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        {cfg.pending_configuration_notice ? "Configuración pendiente: hay incidencias sensibles registradas que esperan a un responsable designado. " : "Aún no hay un responsable de incidencias sensibles activo. "}
        Desígnelo en la pestaña «Incidencias sensibles».
      </span>
    </div>
  );
};

const StatusCard = ({ cfg }) => (
  <div className={`rounded-xl border p-4 ${cfg.valid ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="incident-responsible-status">
    <div className="flex items-center gap-2 mb-1">
      {cfg.valid ? <ShieldCheck className="w-4 h-4 text-emerald-700" /> : <ShieldAlert className="w-4 h-4 text-amber-700" />}
      <span className="font-semibold text-gray-900">{cfg.valid ? "Responsable activo" : "Sin responsable activo"}</span>
    </div>
    {cfg.responsible ? (
      <p className="text-sm text-gray-700" data-testid="incident-responsible-name">
        {cfg.responsible.name} · {cfg.responsible.email}{cfg.responsible.professional_role ? ` · ${cfg.responsible.professional_role}` : ""} · desde {fmt(cfg.designated_at)}
      </p>
    ) : (
      <p className="text-sm text-gray-700">{cfg.invalid_reason || "Designe a un docente activo de su colegio."}</p>
    )}
    {cfg.pending_configuration_notice && <p className="text-xs text-amber-800 mt-2" data-testid="incident-pending-notice">Hay incidencias registradas esperando a un responsable.</p>}
  </div>
);

const History = ({ rows }) => (
  <div className="text-sm" data-testid="incident-designation-history">
    <h4 className="font-semibold text-gray-900 mb-2">Historial de designaciones</h4>
    {rows.length === 0 ? <p className="text-gray-400">Sin designaciones.</p> : (
      <ul className="space-y-1">
        {rows.map((h, i) => (
          <li key={i} className="flex flex-wrap gap-2 text-gray-600">
            <Badge variant="outline">{h.active ? "Activa" : "Revocada"}</Badge>
            <span>{h.responsible_name}{h.professional_role ? ` (${h.professional_role})` : ""}</span>
            <span className="text-gray-400">designada {fmt(h.designated_at)} por {h.designated_by_name}</span>
            {h.revoked_at && <span className="text-gray-400">· revocada {fmt(h.revoked_at)} por {h.revoked_by_name}</span>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const IncidentResponsiblePanel = () => {
  const [cfg, reload] = useIncidentConfig();
  const [candidates, setCandidates] = useState([]);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("");
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/institution/incident-responsible/candidates`, { withCredentials: true })
      .then((r) => setCandidates(r.data || [])).catch(() => setCandidates([]));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/institution/incident-responsible`,
        { user_id: userId, attestation_confirmed: attest, professional_role: role }, { withCredentials: true });
      toast.success(data.released_pending ? `Responsable designado. ${data.released_pending} incidencia(s) pendiente(s) quedaron disponibles en su bandeja.` : "Responsable designado.");
      setUserId(""); setRole(""); setAttest(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo designar"); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("¿Retirar al responsable? Las nuevas incidencias quedarán pendientes hasta designar a otra persona.")) return;
    try { await axios.post(`${API}/institution/incident-responsible/revoke`, {}, { withCredentials: true }); toast.success("Designación retirada."); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo retirar"); }
  };

  if (!cfg) return <p className="text-sm text-gray-400">Cargando…</p>;
  return (
    <div className="space-y-6" data-testid="incident-responsible-panel">
      <div className="flex items-start gap-3 text-sm text-gray-600">
        <Lock className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
        <p>Usted designa quién recibe las incidencias sensibles de su colegio. Como administrador no puede consultar su contenido. QHUB.BUDDY registra las incidencias y las dirige al responsable; no ofrece vigilancia permanente, revisión inmediata ni avisos automáticos a familias o autoridades.</p>
      </div>
      <StatusCard cfg={cfg} />
      <div className="rounded-xl border border-gray-100 p-4 space-y-3">
        <h4 className="font-semibold text-gray-900">{cfg.responsible ? "Cambiar responsable" : "Designar responsable"}</h4>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger data-testid="incident-candidate-select"><SelectValue placeholder="Seleccione un docente activo" /></SelectTrigger>
          <SelectContent>
            {candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={role} onChange={(e) => setRole(e.target.value)} maxLength={80} placeholder="Función (opcional): orientadora, psicólogo escolar…" data-testid="incident-professional-role-input" />
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <Checkbox checked={attest} onCheckedChange={(v) => setAttest(Boolean(v))} data-testid="incident-attestation-checkbox" className="mt-0.5" />
          <span>{cfg.attestation_text}</span>
        </label>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={submit} disabled={!userId || !attest || busy} data-testid="incident-designate-btn">{cfg.responsible ? "Cambiar responsable" : "Designar"}</Button>
          {cfg.configured && <Button variant="outline" onClick={revoke} data-testid="incident-revoke-btn">Retirar designación</Button>}
        </div>
      </div>
      <History rows={cfg.history || []} />
    </div>
  );
};
